import React, { useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getStripeConfig } from '../../api/billing';

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = getStripeConfig().then((config) =>
      config?.publishableKey ? loadStripe(config.publishableKey) : null
    );
  }
  return stripePromise;
}

interface CheckoutFormProps {
  plan: string;
  intentType: 'setup' | 'payment';
  onSuccess: (intentId: string) => void;
  onCancel: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ plan, intentType, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    if (intentType === 'setup') {
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/billing?success=true',
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment setup failed');
        setLoading(false);
        return;
      }

      if (setupIntent) {
        onSuccess(setupIntent.id);
      }
    } else {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/billing?success=true',
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed');
        setLoading(false);
        return;
      }

      if (paymentIntent) {
        onSuccess(paymentIntent.id);
      }
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 24 }}>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {error && (
        <p style={{ color: '#f87171', fontSize: 14, marginBottom: 16 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            color: '#d1d5db',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          style={{
            padding: '10px 24px',
            borderRadius: 8,
            border: 'none',
            background: loading ? '#4f46e5' : '#6366f1',
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            opacity: loading || !stripe ? 0.7 : 1,
          }}
        >
          {loading ? 'Processing...' : 'Confirm Payment'}
        </button>
      </div>
    </form>
  );
};

interface PaymentModalProps {
  open: boolean;
  clientSecret: string;
  plan: string;
  planName: string;
  planPrice: string;
  intentType: 'setup' | 'payment';
  onSuccess: (intentId: string) => void;
  onClose: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  clientSecret,
  plan,
  planName,
  planPrice,
  intentType,
  onSuccess,
  onClose,
}) => {
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  React.useEffect(() => {
    if (open) {
      getStripePromise().then((s) => {
        setStripeInstance(s);
        setStripeLoading(false);
      });
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#111118',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16,
          padding: 32,
          maxWidth: 480,
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ color: '#fff', margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>
          Upgrade to {planName}
        </h2>
        <p style={{ color: '#9ca3af', margin: '0 0 24px', fontSize: 14 }}>
          {planPrice}/month — Enter your payment details below.
        </p>

        {stripeLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            Loading payment form...
          </div>
        ) : stripeInstance && clientSecret ? (
          <Elements
            stripe={stripeInstance}
            options={{
              clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#6366f1',
                  colorBackground: '#1a1a24',
                  colorText: '#e5e7eb',
                  colorDanger: '#f87171',
                  borderRadius: '8px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                },
              },
            }}
          >
            <CheckoutForm
              plan={plan}
              intentType={intentType}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        ) : (
          <p style={{ color: '#f87171', textAlign: 'center', padding: 20 }}>
            Failed to load payment form. Please try again.
          </p>
        )}
      </div>
    </div>
  );
};
